import React, { useCallback, useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { AdminUserRow } from "@/components/AdminUserRow";

const COPY = {
  en: { title: "Tester access", sub: "Give signed-up users unlimited consultations for testing.", search: "Search by email, name or company", none: "No users found.", denied: "Only admins can open this page." },
  fr: { title: "Accès testeurs", sub: "Donnez aux utilisateurs inscrits des consultations illimitées pour les tests.", search: "Rechercher par e-mail, nom ou société", none: "Aucun utilisateur trouvé.", denied: "Seuls les administrateurs peuvent ouvrir cette page." },
  ar: { title: "وصول المختبِرين", sub: "امنح المستخدمين المسجّلين استشارات غير محدودة للاختبار.", search: "ابحث بالبريد أو الاسم أو الشركة", none: "لا يوجد مستخدمون.", denied: "هذه الصفحة للمشرفين فقط." },
};

export default function Admin() {
  const { user } = useAuth();
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (query) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: { q: query || undefined } });
      setUsers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (user?.role !== "admin") return;
    const id = setTimeout(() => load(q), 300);
    return () => clearTimeout(id);
  }, [q, load, user]);

  const onUpdated = (u) => setUsers((list) => list.map((x) => (x.user_id === u.user_id ? u : x)));

  if (user?.role !== "admin") {
    return <AppShell><p className="text-[#495057]" data-testid="admin-denied">{c.denied}</p></AppShell>;
  }

  return (
    <AppShell>
      <div className="editorial-rule pt-4">
        <h1 className="font-display text-4xl">{c.title}</h1>
        <p className="mt-2 text-[#495057]">{c.sub}</p>
      </div>
      <div className="mt-8 relative max-w-xl">
        <Search className="w-4 h-4 text-[#868E96] absolute start-4 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={c.search} data-testid="admin-search-input"
          className="w-full border border-[#E9ECEF] bg-white ps-11 pe-4 py-3 focus:outline-none focus:border-[#0A1128]" />
      </div>
      <div className="mt-6 bg-white border border-[#E9ECEF] divide-y divide-[#E9ECEF]" data-testid="admin-user-list">
        {!loading && users.length === 0 && <p className="p-6 text-[#868E96]" data-testid="admin-no-users">{c.none}</p>}
        {users.map((u) => <AdminUserRow key={u.user_id} u={u} lang={lang} onUpdated={onUpdated} />)}
      </div>
    </AppShell>
  );
}
